// ╔══════════════════════════════════════════════════════════════╗
// ║  Auth context — manages user + access token globally          ║
// ╚══════════════════════════════════════════════════════════════╝

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, getAccessToken, setAccessToken } from './api.js';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: try refresh if a previous session existed
  useEffect(() => {
    const hadToken = localStorage.getItem('hindam_token_present');
    if (!hadToken) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await api.post('/auth/refresh');
        setAccessToken(r.data.accessToken);
        setUser(r.data.user);
      } catch {
        setAccessToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Listen for global logout (triggered by API interceptor on 401)
  useEffect(() => {
    const onLogout = () => {
      setUser(null);
      setAccessToken(null);
    };
    window.addEventListener('hindam:logged-out', onLogout);
    return () => window.removeEventListener('hindam:logged-out', onLogout);
  }, []);

  // Proactive silent token refresh — runs every 110 minutes (10 min before
  // the 2-hour access token expires). Fewer refresh calls = fewer chances
  // for a transient network failure to disturb the user.
  //
  // IMPORTANT: a single failed proactive refresh must NOT log the user out.
  // It can fail transiently (network blip, race with focus-refresh, etc.).
  // The real logout signal is the api.js 401-interceptor when an actual API
  // call fails after a retry — that already fires `hindam:logged-out`.
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await api.post('/auth/refresh');
        setAccessToken(r.data.accessToken);
      } catch {
        /* silent — let the next real API call drive the logout if needed */
      }
    }, 110 * 60 * 1000);
    return () => clearInterval(id);
  }, [user]);

  const login = useCallback(async (identifier, password) => {
    const r = await api.post('/auth/login', { identifier, password });
    setAccessToken(r.data.accessToken);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    setAccessToken(null);
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
